import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckBox, Spinner, Text } from "@ui-kitten/components";
import AutoRenewalConsentSection from "components/AutoRenewalConsentSection";
import Button from "components/Button";
import Container from "components/Container";
import Footer from "components/Footer";
import Header from "components/Header";
import TenantLogo from "components/TenantLogo";
import { AgreementDetails } from "features/types";
import useAuth from "hooks/useAuth";
import { RenewalOptInScreenProps } from "navigation/types";
import {
  fetchRenewalOptInDetailsFetcher,
  redeemRenewalOptInFetcher,
} from "queries/account.queries";
import React, { useState } from "react";
import { View } from "react-native";

const errorMessageFor = (error: unknown) => {
  const code = (error as any)?.response?.data?.code;
  return code === "OPT_IN_TOKEN_EXPIRED"
    ? "This opt-in link has expired. Please log in to your account to manage your renewal preferences, or contact support."
    : "This opt-in link is not valid. Please log in to your account to manage your renewal preferences, or contact support.";
};

// Landing page for the opt-in link in renewal opt-in notice emails. The link
// alone never opts the member in: the page first shows the plan and the
// auto-renewal terms, and the member must check the consent box and press
// Opt In before the POST fires.
const RenewalOptIn = ({ navigation, route }: RenewalOptInScreenProps) => {
  const { token } = route.params;
  const { isLoggedIn } = useAuth();
  const [consentGiven, setConsentGiven] = useState(false);

  const {
    data: details,
    isLoading: detailsLoading,
    isError: detailsIsError,
    error: detailsError,
  } = useQuery(
    ["renewalOptIn", token],
    () => fetchRenewalOptInDetailsFetcher(token),
    { retry: false, staleTime: Infinity },
  );

  const {
    mutateAsync: redeem,
    data: redeemed,
    isLoading: redeeming,
    isError: redeemIsError,
    error: redeemError,
  } = useMutation(redeemRenewalOptInFetcher);

  const onOptIn = () => {
    redeem(token).catch(() => {
      // Errors are rendered inline below.
    });
  };

  const goToAccount = () => {
    if (isLoggedIn) {
      navigation.navigate("AuthenticatedFlow", {
        screen: "MainFlow",
        params: {
          screen: "Account",
          params: {
            screen: "Settings",
            params: { screen: "SettingScreen" },
          },
        },
      });
    } else {
      navigation.navigate("LoginFlow", { screen: "Login" });
    }
  };

  const agreements: AgreementDetails[] | undefined = details?.termsBody
    ? [{ id: 0, body: details.termsBody } as AgreementDetails]
    : undefined;

  const isError = detailsIsError || redeemIsError;
  const error = detailsIsError ? detailsError : redeemError;
  const optedIn = !!redeemed || !!details?.alreadyRedeemed;
  const showConfirmStep = !!details && !optedIn && !isError;

  return (
    <View className="flex-1">
      <Header />
      <Container className="flex-grow items-center">
        <View className="max-w-3xl w-full">
          <View className="items-center">
            <TenantLogo width={125} height={125} />
          </View>
          <View className="mt-8 items-center">
            {detailsLoading && <Spinner size="large" />}
            {showConfirmStep && (
              <View className="items-center">
                <Text category="h6" style={{ textAlign: "center" }}>
                  Opt Back In to Auto-Renewal
                </Text>
                <Text style={{ textAlign: "center", marginTop: 12 }}>
                  Turn automatic renewal back on for your{" "}
                  {details.planPackageName}. Confirm below to opt back in.
                </Text>
                <View style={{ marginTop: 24, alignSelf: "stretch" }}>
                  {agreements ? (
                    <AutoRenewalConsentSection
                      agreements={agreements}
                      consentGiven={consentGiven}
                      onChange={setConsentGiven}
                    />
                  ) : (
                    <CheckBox
                      checked={consentGiven}
                      onChange={setConsentGiven}
                      style={{ marginBottom: 16 }}
                    >
                      I want to opt back in to automatic renewal.
                    </CheckBox>
                  )}
                  <Button
                    disabled={!consentGiven}
                    loading={redeeming}
                    onPress={onOptIn}
                  >
                    Opt In
                  </Button>
                </View>
              </View>
            )}
            {optedIn && (
              <View className="items-center">
                <Text category="h6" style={{ textAlign: "center" }}>
                  You're All Set!
                </Text>
                <Text style={{ textAlign: "center", marginTop: 12 }}>
                  You are opted in for auto-renewal for your{" "}
                  {(redeemed ?? details)?.planPackageName ?? "plan"}. You can make any
                  changes to your account or renewal status in your Account
                  settings.
                </Text>
                <Button style={{ marginTop: 24 }} onPress={goToAccount}>
                  Go to Account Settings
                </Button>
              </View>
            )}
            {isError && (
              <View className="items-center">
                <Text category="h6" style={{ textAlign: "center" }}>
                  Something Went Wrong
                </Text>
                <Text style={{ textAlign: "center", marginTop: 12 }}>
                  {errorMessageFor(error)}
                </Text>
                <Button style={{ marginTop: 24 }} onPress={goToAccount}>
                  Go to Account Settings
                </Button>
              </View>
            )}
          </View>
        </View>
      </Container>
      <Footer />
    </View>
  );
};

export default RenewalOptIn;
